import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Search, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useData } from "../DataContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products", hasDropdown: true },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" }
];

export default function Header() {
  const { PRODUCT_CATEGORIES } = useData();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [megaCat, setMegaCat] = useState(PRODUCT_CATEGORIES[0].id);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"}`}>
      {/* top strip */}
      <div className="hidden md:block bg-[#0a2a5e] text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 flex justify-between py-2">
          <span>India's Trusted Tanks and Pipes Brand</span>
          <div className="flex items-center gap-5">
            <a href="tel:18002023360" className="flex items-center gap-1.5 hover:text-sky-300 transition-colors">
              <Phone className="h-3.5 w-3.5" /> 1800 202 3360
            </a>
            <Link to="/contact" className="hover:text-sky-300 transition-colors">Dealer Locator</Link>
            <Link to="/contact" className="hover:text-sky-300 transition-colors">Careers</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/baishalii-logo.png" alt="BAISHALII — A name that justifies TRUST" className="h-14 w-auto" data-testid="header-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <div key={l.to} className="relative" onMouseEnter={() => l.hasDropdown && setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <NavLink
                to={l.to}
                className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-1 ${isActive ? "text-sky-600" : "text-slate-700 hover:text-sky-600"}`}
              >
                {l.label}
                {l.hasDropdown && <ChevronDown className="h-3.5 w-3.5" />}
              </NavLink>
              {l.hasDropdown && productsOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[880px]" data-testid="products-mega-menu">
                  <div className="bg-white rounded-xl shadow-2xl border border-slate-100 flex overflow-hidden">
                    <div className="w-[280px] bg-slate-50 border-r border-slate-100 py-3">
                      {PRODUCT_CATEGORIES.map((c) => (
                        <Link
                          key={c.id}
                          to={`/products#${c.id}`}
                          onMouseEnter={() => setMegaCat(c.id)}
                          data-testid={`mega-cat-${c.id}`}
                          className={`flex items-center justify-between px-5 py-2.5 text-sm font-medium transition-colors ${megaCat === c.id ? "bg-white text-sky-700 border-l-2 border-sky-500" : "text-slate-700 hover:bg-white hover:text-sky-700"}`}
                        >
                          {c.name}
                          <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-slate-400" />
                        </Link>
                      ))}
                    </div>
                    <div className="flex-1 p-5 grid grid-cols-3 gap-4 content-start max-h-[440px] overflow-y-auto" data-testid="mega-products-panel">
                      {(PRODUCT_CATEGORIES.find((c) => c.id === megaCat)?.products || []).map((p) => (
                        <Link key={p.slug} to={`/products/${p.slug}`} className="group text-center rounded-lg p-2 hover:bg-sky-50 transition-colors" data-testid={`mega-product-${p.slug}`}>
                          <div className="h-24 flex items-center justify-center mb-2">
                            <img src={p.image} alt={p.name} className="max-h-24 max-w-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                          </div>
                          <div className="text-sm font-bold text-[#0a2a5e] group-hover:text-sky-700">{p.name}</div>
                          <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{p.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Search">
            <Search className="h-4 w-4 text-slate-600" />
          </button>
          <Button asChild className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full px-5 shadow-md">
            <Link to="/contact">Enquire Now</Link>
          </Button>
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-700" aria-label="Menu">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-slate-700 font-semibold hover:bg-sky-50 hover:text-sky-700 transition-colors">
              {l.label}
            </NavLink>
          ))}
          <Button asChild className="w-full mt-3 bg-gradient-to-r from-sky-500 to-[#0a2a5e] text-white rounded-full">
            <Link to="/contact" onClick={() => setMobileOpen(false)}>Enquire Now</Link>
          </Button>
        </div>
      )}
    </header>
  );
}
